import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Save, X, Package, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getImageUrl } from '../utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const AdminProductsPage = () => {
  const [fabrics, setFabrics] = useState([]);
  const [souvenirs, setSouvenirs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fabrics');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    branded_price: '',
    image_url: '',
    description: '',
    is_active: true,
    moq_value: '1',
    has_branding: false  // For souvenirs: enable branding option
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const [fabricsRes, souvenirsRes] = await Promise.all([
        axios.get(`${API_URL}/api/fabrics`),
        axios.get(`${API_URL}/api/souvenirs`)
      ]);
      setFabrics(fabricsRes.data || []);
      setSouvenirs(souvenirsRes.data || []);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error('Please fill in name and price');
      return;
    }

    const imageUrl = formData.image_url || `https://placehold.co/400x500/e2e8f0/64748b?text=${encodeURIComponent(formData.name)}`;
    
    const endpoint = activeTab === 'fabrics' 
      ? `${API_URL}/api/admin/fabrics`
      : `${API_URL}/api/admin/souvenirs`;

    // Prepare MOQ value based on product type
    const moqValue = activeTab === 'fabrics' 
      ? parseFloat(formData.moq_value) || 1 
      : parseInt(formData.moq_value) || 1;
    
    const unitType = activeTab === 'fabrics' ? 'yard' : 'piece';

    try {
      if (editingItem) {
        await axios.put(`${endpoint}/${editingItem.id}`, {
          ...formData,
          price: parseFloat(formData.price),
          branded_price: formData.branded_price ? parseFloat(formData.branded_price) : null,
          image_url: imageUrl,
          moq_value: moqValue,
          unit_type: unitType,
          has_branding: activeTab === 'souvenirs' ? formData.has_branding : undefined
        }, { headers });
        toast.success('Product updated!');
      } else {
        await axios.post(endpoint, {
          ...formData,
          price: parseFloat(formData.price),
          branded_price: formData.branded_price ? parseFloat(formData.branded_price) : null,
          image_url: imageUrl,
          moq_value: moqValue,
          unit_type: unitType,
          has_branding: activeTab === 'souvenirs' ? formData.has_branding : undefined
        }, { headers });
        toast.success('Product added!');
      }
      
      setShowModal(false);
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    
    const endpoint = activeTab === 'fabrics'
      ? `${API_URL}/api/admin/fabrics/${item.id}`
      : `${API_URL}/api/admin/souvenirs/${item.id}`;

    try {
      await axios.delete(endpoint, { headers });
      toast.success('Product deleted!');
      loadProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      price: item.price?.toString() || '',
      branded_price: item.branded_price?.toString() || '',
      image_url: item.image_url || '',
      description: item.description || '',
      is_active: item.is_active !== false,
      moq_value: item.moq_value?.toString() || '1',
      has_branding: item.has_branding || false
    });
    // Set preview from existing image
    if (item.image_url) {
      setImagePreview(getImageUrl(item.image_url));
    } else {
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', branded_price: '', image_url: '', description: '', is_active: true, moq_value: '1', has_branding: false });
    setEditingItem(null);
    setImagePreview(null);
  };

  // Handle image file selection
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload JPG, PNG, or WebP image only');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File exceeds maximum size of 5MB');
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);

    // Upload file
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/admin/upload/product-image`,
        uploadFormData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Image upload failed. Please try again.');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  // Remove/clear image
  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const currentItems = activeTab === 'fabrics' ? fabrics : souvenirs;

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-products-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Products Management</h1>
        <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Add {activeTab === 'fabrics' ? 'Fabric' : 'Souvenir'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button 
          variant={activeTab === 'fabrics' ? 'default' : 'outline'}
          onClick={() => setActiveTab('fabrics')}
        >
          Fabrics ({fabrics.length})
        </Button>
        <Button 
          variant={activeTab === 'souvenirs' ? 'default' : 'outline'}
          onClick={() => setActiveTab('souvenirs')}
        >
          Souvenirs ({souvenirs.length})
        </Button>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentItems.map((item) => (
          <Card key={item.id} className="overflow-hidden">
            <img 
              src={getImageUrl(item.image_url)} 
              alt={item.name}
              className="w-full h-40 object-cover bg-zinc-100"
              onError={(e) => { e.target.src = `https://placehold.co/400x300/e2e8f0/64748b?text=${encodeURIComponent(item.name)}`; }}
            />
            <CardContent className="p-4">
              <h3 className="font-semibold">{item.name}</h3>
              <p className="text-sm text-zinc-600">₦{item.price?.toLocaleString()}</p>
              {item.branded_price && (
                <p className="text-xs text-zinc-500">Branded: ₦{item.branded_price?.toLocaleString()}</p>
              )}
              <p className="text-xs text-zinc-500 mt-1">
                MOQ: {item.moq_value || 1} {item.unit_type === 'yard' ? 'Yards' : 'Pieces'}
              </p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => startEdit(item)}>
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(item)}>
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {currentItems.length === 0 && (
        <div className="text-center py-12 text-zinc-500">
          <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No {activeTab} found. Add your first product!</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-md p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {editingItem ? 'Edit' : 'Add'} {activeTab === 'fabrics' ? 'Fabric' : 'Souvenir'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-zinc-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Price (₦) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Branded Price (₦)</label>
                  <input
                    type="number"
                    value={formData.branded_price}
                    onChange={(e) => setFormData({...formData, branded_price: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Has Branding Toggle - Only for Souvenirs */}
              {activeTab === 'souvenirs' && (
                <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="has-branding"
                    checked={formData.has_branding}
                    onChange={(e) => setFormData({...formData, has_branding: e.target.checked})}
                    className="w-4 h-4 rounded border-zinc-300 text-[#D90429] focus:ring-[#D90429]"
                    data-testid="has-branding-toggle"
                  />
                  <label htmlFor="has-branding" className="text-sm">
                    <span className="font-medium">Enable Branding Option</span>
                    <p className="text-xs text-zinc-500">Customers can choose between unbranded (regular price) and branded (branded price)</p>
                  </label>
                </div>
              )}
              
              {/* MOQ Field - Below Price, Above Image */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Minimum Order Quantity ({activeTab === 'fabrics' ? 'Yards' : 'Pieces'}) *
                </label>
                <input
                  type="number"
                  value={formData.moq_value}
                  onChange={(e) => setFormData({...formData, moq_value: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  min={activeTab === 'fabrics' ? '0.1' : '1'}
                  step={activeTab === 'fabrics' ? '0.5' : '1'}
                  placeholder={activeTab === 'fabrics' ? 'e.g., 1.5' : 'e.g., 20'}
                  required
                  data-testid="moq-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  {activeTab === 'fabrics' 
                    ? 'Minimum yards customers must order (decimals allowed)' 
                    : 'Minimum pieces customers must order (whole numbers only)'}
                </p>
              </div>
              
              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium mb-1">Product Image</label>
                <div className="border-2 border-dashed border-zinc-300 rounded-lg p-4 text-center">
                  {/* Preview */}
                  {(imagePreview || formData.image_url) && (
                    <div className="relative inline-block mb-3">
                      <img 
                        src={imagePreview || getImageUrl(formData.image_url)} 
                        alt="Preview" 
                        className="h-32 w-32 object-cover rounded-lg mx-auto"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        title="Remove image"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="product-image-upload"
                  />
                  <label
                    htmlFor="product-image-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 rounded-lg cursor-pointer hover:bg-zinc-50 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-zinc-400 border-t-transparent"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        {(imagePreview || formData.image_url) ? 'Replace Image' : 'Choose File'}
                      </>
                    )}
                  </label>
                  <p className="text-xs text-zinc-400 mt-2">JPG, PNG, WebP • Max 5MB</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;
